/* =============================================================
   Link tap feedback
   Keeps the tapped link tinted (class "link-tapped", styled in
   critical.css) so users can see which link they activated while
   the next page is loading.

   The class is applied on pointerdown — not on click — so the
   highlight does not blink between the browser's :active state
   ending (finger up) and the click event firing. On touch, a short
   delay filters out scroll flicks (mirroring native :active), and
   pointercancel clears the highlight when a scroll takes over.

   Links that do not unload the page (new tab, hash, prevented
   default) only flash briefly and then reset.
   ============================================================= */
(function () {
  'use strict';

  var CLASS = 'link-tapped';
  var FLASH_MS = 1200;
  var TOUCH_APPLY_DELAY_MS = 80;
  var NO_CLICK_CLEANUP_MS = 500;

  var pending = null; // { link, applyTimer, cleanupTimer }

  function targetLink(event) {
    var link = event.target && event.target.closest ? event.target.closest('a') : null;
    if (!link) return null;
    // Button-styled links keep their own pressed treatment (see critical.css).
    if (link.classList.contains('btn-primary') || link.classList.contains('product-card__cta')) return null;
    return link;
  }

  function apply(link) {
    document.querySelectorAll('.' + CLASS).forEach(function (el) {
      if (el !== link) el.classList.remove(CLASS);
    });
    link.classList.add(CLASS);
  }

  function resetPending(removeClass) {
    if (!pending) return;
    if (pending.applyTimer) clearTimeout(pending.applyTimer);
    if (pending.cleanupTimer) clearTimeout(pending.cleanupTimer);
    if (removeClass) pending.link.classList.remove(CLASS);
    pending = null;
  }

  document.addEventListener('pointerdown', function (event) {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    var link = targetLink(event);
    if (!link) return;

    resetPending(true);
    pending = { link: link, applyTimer: null, cleanupTimer: null };

    if (event.pointerType === 'mouse') {
      apply(link);
    } else {
      // On touch, wait a moment before highlighting so scroll flicks that
      // start on a link do not flash it.
      pending.applyTimer = setTimeout(function () {
        if (pending && pending.link === link) {
          pending.applyTimer = null;
          apply(link);
        }
      }, TOUCH_APPLY_DELAY_MS);
    }
  });

  // The browser turns a press into a scroll (or aborts it) — undo everything.
  document.addEventListener('pointercancel', function () {
    resetPending(true);
  });

  document.addEventListener('pointerup', function (event) {
    if (!pending) return;
    var link = pending.link;

    // Fast tap: the touch delay has not elapsed yet — highlight now so the
    // class is already in place when click fires.
    if (pending.applyTimer) {
      clearTimeout(pending.applyTimer);
      pending.applyTimer = null;
      apply(link);
    }

    // Released outside the pressed link: no click will follow for it.
    if (targetLink(event) !== link) {
      resetPending(true);
      return;
    }

    // Safety net: if no click confirms the tap, clean up shortly after.
    pending.cleanupTimer = setTimeout(function () {
      if (pending && pending.link === link) resetPending(true);
    }, NO_CLICK_CLEANUP_MS);
  });

  function unloadsPage(link, event) {
    if (event.defaultPrevented) return false;
    // Modifier clicks open a new tab/window; the current page stays.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;
    var href = link.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return false;
    if (/^(javascript|mailto|tel):/i.test(href)) return false;
    return true;
  }

  document.addEventListener('click', function (event) {
    var link = targetLink(event);
    if (!link) return;

    resetPending(false); // the tap is confirmed — keep the class on
    apply(link);

    if (!unloadsPage(link, event)) {
      setTimeout(function () { link.classList.remove(CLASS); }, FLASH_MS);
    }
  });

  // Returning via the back/forward cache restores the DOM as it was,
  // including the last tapped link — reset it.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      document.querySelectorAll('.' + CLASS).forEach(function (el) {
        el.classList.remove(CLASS);
      });
    }
  });
})();
